import { useState } from 'react'
import { Link } from 'react-router'
import { Text } from '@carouselgg/nexus/text'
import { useAuthStore } from '@carouselgg/auth/store'
import styles from './TrustScoreChip.module.css'

function scoreGradient(score: number): { from: string; to: string } {
  if (score >= 80) return { from: 'var(--nexus-green)', to: 'var(--nexus-blue)' }
  if (score >= 50) return { from: 'var(--nexus-blue)', to: 'var(--nexus-purple)' }
  if (score >= 20) return { from: 'var(--nexus-yellow)', to: 'var(--nexus-pink)' }
  return { from: 'var(--nexus-pink)', to: 'var(--nexus-pink-active)' }
}

function scoreTextColor(score: number): string {
  if (score >= 80) return 'var(--nexus-green)'
  if (score >= 50) return 'var(--nexus-blue)'
  if (score >= 20) return 'var(--nexus-yellow)'
  return 'var(--nexus-pink)'
}

interface TrustScoreChipProps {
  size?: 'sm' | 'md' | 'ring'
}

let gradientIdCounter = 0

export function TrustScoreChip({ size = 'md' }: TrustScoreChipProps) {
  const trustScore = useAuthStore((s) => s.trustScore)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const [gradientId] = useState(() => `trustChipGrad-${++gradientIdCounter}`)

  if (!isAuthenticated || !trustScore) return null

  const score = trustScore.score
  const gradient = scoreGradient(score)
  const textColor = scoreTextColor(score)
  const ringSize = size === 'md' ? 36 : size === 'ring' ? 28 : 24
  const strokeWidth = size === 'md' ? 3 : 2.5
  const fontSize = size === 'md' ? 11 : size === 'ring' ? 9 : 8
  const radius = (ringSize - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const progress = (score / 100) * circumference
  const center = ringSize / 2

  return (
    <Link to="/trust" className={styles.chip} data-size={size}>
      <svg width={ringSize} height={ringSize} className={styles.ring}>
        <defs>
          <linearGradient id={gradientId} x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor={gradient.from} />
            <stop offset="100%" stopColor={gradient.to} />
          </linearGradient>
        </defs>
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke="var(--nexus-border)"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={`url(#${gradientId})`}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={circumference - progress}
          strokeLinecap="round"
          transform={`rotate(-90 ${center} ${center})`}
        />
        <text
          x={center}
          y={center}
          textAnchor="middle"
          dominantBaseline="central"
          fill={textColor}
          fontSize={fontSize}
          fontWeight="700"
        >
          {score}
        </text>
      </svg>
      {size !== 'ring' && <Text size="xs" muted className={styles.level}>Your Trust Score</Text>}
    </Link>
  )
}
